import { IRoom, ISubscription } from "@eventr/common";
import { create } from "../appState/resetState";
import { IRCUser, IRCUserInfo } from "../../data/Rocket";

/**
 * The chat data every RocketChatProvider instance shows, and which instance does the work.
 *
 * On iOS native navigation every pushed view portals its own copy of the React subtree, so
 * RocketChatProvider mounts once per view (it must: openChat navigates in its own view's
 * stack). With its state in useState and its effects run by every copy, each view fetched
 * rooms on its own (eight `rooms/get` at once, most rate-limited), while the socket's single
 * `onmessage` fed only the last copy - so the Messages view could sit on stale or empty
 * rooms. The data lives here, module-scoped like the QueryClient, and one mounted instance -
 * the leader - runs the socket and network effects for all of them.
 */
interface ChatDataState {
  userRooms?: IRoom[];
  userSubscriptions?: ISubscription[];
  rcUserList?: IRCUser[];
  rcUserInfo?: IRCUserInfo;
  loadingChat: boolean;
  roomsFetched: boolean;
  /** The instance running the effects; null until one claims it (and again after logout). */
  leader: symbol | null;
}

export const useChatDataStore = create<ChatDataState>()(() => ({
  loadingChat: true,
  roomsFetched: false,
  leader: null,
}));

type Updater<T> = T | ((previous: T) => T);

/** A stable, useState-style setter for one field, so call sites read as before. */
const setterFor =
  <K extends keyof ChatDataState>(key: K) =>
  (update: Updater<ChatDataState[K]>) =>
    useChatDataStore.setState((state) => ({
      [key]:
        typeof update === "function"
          ? (update as (previous: ChatDataState[K]) => ChatDataState[K])(state[key])
          : update,
    }));

export const setUserRooms = setterFor("userRooms");
export const setUserSubscriptions = setterFor("userSubscriptions");
export const setRcUserList = setterFor("rcUserList");
export const setRcUserInfo = setterFor("rcUserInfo");
export const setLoadingChat = setterFor("loadingChat");
export const setRoomsFetched = setterFor("roomsFetched");
